import React from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence } from 'framer-motion';
import Popover from '../Popover/Popover';
import tailwindConfig from '../../../tailwind.config';
import { withRouter } from '../../libs/widgetUtilities';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { dateFormatOptions } from '../../libs/dateFormatting';
import Button from '../Button/Button';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import Sticky from '../../styled components/Sticky';
import { UIBar, UIBarGrowItem } from '../../styled components/UIBar';
import { Caption, Link, Meta } from '../../styled components/Typography';

import UICard from '../../styled components/UICard';

import {
  NoNotificationsContainer,
  NoNotificationsIcon,
  NoNotificationsText,
  NoNotificationsTitle,
  NotificationCenterNoContent,
  NotificationItemCategoryText,
  NotificationsBar,
  NotificationsList,
  NotificationsListDivider,
  NotificationsListItemText,
  NotificationsOnDateDateText,
  NotificationsOnDateList,
  NotificationsOnDateListItem,
  NotificationsOnDateListItemAnimation,
  NotificationsOnDateNotificationList,
  NotificationsOnDateNotificationsListAnimation,
  NotificationsWithHeaderAnimation,
  UIUpdateCard,
  NotificationTypeTag
} from './NotificationCenter.style';
import Checkbox from '../Checkbox/Checkbox';
import {
  serializeYesplanAPIDate,
  YPDateTimeObjectPropType
} from '../../libs/dateTimeUtilities';
import NavigationHistory from '../../libs/NavigationHistory';
import {
  HeaderActionsRight,
  Header,
  HeaderTitle,
  HeaderContent
} from '../PageHeaders/PageHeader.style';

/* Animations */
const defaultDuration = 0.2;
const NotificationSectionVariants = {
  initial: {
    opacity: 0,
    originY: 0
  },
  enter: {
    originY: 0,
    opacity: 1,
    scaleY: 1,
    transition: {
      duration: defaultDuration
    }
  },
  exit: {
    originY: 0,
    opacity: 0,
    scaleY: 0,
    transition: { duration: defaultDuration }
  }
};

const NotificationVariants = {
  initial: {
    opacity: 0,
    height: 'auto' // Prevent staggering of Notificationcenter when opening
  },
  enter: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: defaultDuration
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: defaultDuration }
  }
};
/* End animations */

class _NotificationCenter extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showAllNotifications: false
    };

    this.toggleShowAllNotifications = this.toggleShowAllNotifications.bind(this);
    this.handleNotificationClick = this.handleNotificationClick.bind(this);
    this.handleRosterNotificationClick = this.handleRosterNotificationClick.bind(this);
    this.handleTimesheetNotificationClick =
      this.handleTimesheetNotificationClick.bind(this);
    this.renderNotificationCenter = this.renderNotificationCenter.bind(this);
    this.renderRosterPublicationNotification =
      this.renderRosterPublicationNotification.bind(this);
    this.renderUpdateNotification = this.renderUpdateNotification.bind(this);
    this.renderNotificationOnDate = this.renderNotificationOnDate.bind(this);
  }

  handleNotificationClick(dateObjOrNull, notification, subpath, title) {
    const {
      linkUrlFor,
      dateFormatSetting,
      toggleNotificationUnreadStatus,
      navigationHistory
    } = this.props;
    let navigationDate = dateObjOrNull ? serializeYesplanAPIDate(dateObjOrNull) : false;
    if (notification && notification.isPublishOrRevokeNotification()) {
      navigationDate = notification.getDateStringForRosterURL(dateFormatSetting);
    }
    if (notification) toggleNotificationUnreadStatus(notification, true);
    navigationHistory.goToWidgetFromPopover(linkUrlFor(`/${subpath}/${navigationDate}`), {
      query: {
        title,
        date: dateObjOrNull ? serializeYesplanAPIDate(dateObjOrNull) : undefined
      }
    });
  }

  handleRosterNotificationClick(dateObjOrNull, notification) {
    this.handleNotificationClick(dateObjOrNull, notification, 'roster', 'Roster');
  }

  handleTimesheetNotificationClick(dateObjOrNull, notification) {
    this.handleNotificationClick(dateObjOrNull, notification, 'timesheet', 'Timesheet');
  }

  toggleShowAllNotifications() {
    const { showAllNotifications } = this.state;
    this.setState({
      showAllNotifications: !showAllNotifications
    });
  }

  renderReadButton(notification) {
    const { toggleNotificationUnreadStatus } = this.props;
    if (notification.getIsRead()) return null;
    return (
      <Checkbox
        variant="rounded"
        onChange={(event) => {
          event.stopPropagation();
          toggleNotificationUnreadStatus(notification, true);
        }}
      />
    );
  }

  renderNotificationOnDate(
    dateObject,
    onClickFunction,
    notificationsToRender,
    notification,
    sectionKey
  ) {
    const onClickNotification = onClickFunction(dateObject, notification);
    const notifRef = `not${serializeYesplanAPIDate(
      dateObject
    )}${notification.getNotificationIdWithMessageHash()}-${sectionKey}`;
    return (
      <NotificationsOnDateListItemAnimation
        key={notifRef}
        variants={NotificationVariants}
        initial="initial"
        animate="enter"
        exit="exit"
        style={{ overflow: 'hidden' }} // Ensure smooth animation
      >
        <div tw="mb-8">
          <NotificationsListItemText>
            <Link $covert onClick={onClickNotification} data-test-type="notificationLink">
              {notification.printDetailedDescriptionRosterNotification()}
            </Link>
          </NotificationsListItemText>
          <UIBar>
            <Caption as="p" $color="gray dark">
              {notification.printTimeSinceNotificationText()}
            </Caption>
            <UIBarGrowItem />
            {this.renderReadButton(notification)}
          </UIBar>
        </div>
        {notification.shouldPrintDividerAfterNotification(notificationsToRender) && (
          <NotificationsListDivider />
        )}
      </NotificationsOnDateListItemAnimation>
    );
  }

  renderWidgetNotificationsForDay(
    dateObject,
    notifications,
    onClickFunction,
    sectionKey
  ) {
    const { dateFormatSetting } = this.props;
    if (notifications.length > 0) {
      const notificationsToRender = this.state.showAllNotifications
        ? notifications
        : notifications.filter((not) => !not.getIsRead());
      if (notificationsToRender.length === 0) return null;
      const onClickNotification = onClickFunction(dateObject);
      const dateNotifList = `not${serializeYesplanAPIDate(dateObject)}-${sectionKey}`;
      return (
        /* Wrap each notification in its own error boundary, such that non-erroring ones are rendered */
        <ErrorBoundary
          {...this.props}
          key={`err${serializeYesplanAPIDate(dateObject)}-${sectionKey}`}
        >
          <NotificationsOnDateListItemAnimation
            key={dateNotifList}
            variants={NotificationVariants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ overflow: 'hidden' }} // Ensure smooth animation
          >
            <UICard $padding={16}>
              <NotificationsOnDateDateText>
                <Link
                  $covert
                  onClick={onClickNotification}
                  data-test-type="notificationLink"
                >
                  {notificationsToRender[0].printNotificationDate(
                    dateObject,
                    dateFormatSetting
                  )}
                </Link>
              </NotificationsOnDateDateText>
              <NotificationsOnDateNotificationList>
                <AnimatePresence>
                  {notificationsToRender.map((notification) =>
                    this.renderNotificationOnDate(
                      dateObject,
                      onClickFunction,
                      notificationsToRender,
                      notification,
                      sectionKey
                    )
                  )}
                </AnimatePresence>
              </NotificationsOnDateNotificationList>
            </UICard>
          </NotificationsOnDateListItemAnimation>
        </ErrorBoundary>
      );
    }
    return null;
  }

  renderRosterNotificationsForDay(dateObject, notifications) {
    return this.renderWidgetNotificationsForDay(
      dateObject,
      notifications,
      (dateObject, notification) =>
        function () {
          this.handleRosterNotificationClick(dateObject, notification);
        }.bind(this),
      'roster'
    );
  }

  renderTimesheetNotificationsForDay(dateObject, notifications) {
    return this.renderWidgetNotificationsForDay(
      dateObject,
      notifications,
      (dateObject, notification) =>
        function () {
          this.handleTimesheetNotificationClick(dateObject, notification);
        }.bind(this),
      'timesheet'
    );
  }

  renderNoNotifications() {
    const message = this.state.showAllNotifications
      ? i18n('No items')
      : i18n('No unread items');
    return (
      <NoNotificationsContainer
        data-test-no-notifications
        key="nonotification"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {message}
      </NoNotificationsContainer>
    );
  }

  renderWidgetChangeNotifications(key, title, notifications, renderFunction) {
    return (
      <NotificationsOnDateListItem
        key={key}
        variants={NotificationVariants}
        initial="initial"
        animate="enter"
        exit="exit"
        style={{ overflow: 'hidden' }} // Ensure smooth animation
      >
        <NotificationItemCategoryText>
          <Meta as="span">{title}</Meta>
        </NotificationItemCategoryText>
        <NotificationsOnDateNotificationsListAnimation
          key={`${key}changeslist`}
          exit="exit"
        >
          <AnimatePresence>
            {notifications.doFunctionOnWidgetNotificationsForNonEmptyDates(
              renderFunction
            )}
          </AnimatePresence>
        </NotificationsOnDateNotificationsListAnimation>
      </NotificationsOnDateListItem>
    );
  }

  renderRosterChangeNotifications() {
    return this.renderWidgetChangeNotifications(
      'rosterchanges',
      i18n('Roster Changes'),
      this.props.rosternotifications,
      this.renderRosterNotificationsForDay.bind(this)
    );
  }

  renderTimesheetChangeNotifications() {
    return this.renderWidgetChangeNotifications(
      'timesheetchanges',
      i18n('Timesheet Changes'),
      this.props.timesheetnotifications,
      this.renderTimesheetNotificationsForDay.bind(this)
    );
  }

  renderUpdateNotification(notification) {
    const textForTag = notification.printTagForUpdateNotification();
    return (
      <UIUpdateCard $padding={16}>
        <NotificationsListItemText>
          {textForTag && (
            <NotificationTypeTag variant={notification.getTag()}>
              {textForTag}
            </NotificationTypeTag>
          )}
          {notification.getNotificationMessage()}
        </NotificationsListItemText>
        <UIBar>
          <Caption as="p" $color="gray dark">
            {notification.printTimeSinceNotificationText()}
          </Caption>
          <UIBarGrowItem />
          {this.renderReadButton(notification)}
        </UIBar>
      </UIUpdateCard>
    );
  }

  renderRosterPublicationNotification(notification) {
    return (
      <UICard $padding={16}>
        <NotificationsListItemText>
          <Link
            $covert
            onClick={() => this.handleRosterNotificationClick(null, notification)}
            data-test-type="notificationLink"
          >
            {notification.getNotificationText()}
          </Link>
        </NotificationsListItemText>
        <UIBar>
          <Caption as="p" $color="gray dark">
            {notification.printTimeSinceNotificationText()}
          </Caption>
          <UIBarGrowItem />
          {this.renderReadButton(notification)}
        </UIBar>
      </UICard>
    );
  }

  renderNotificationsWithHeader(
    notifications,
    renderHeaderFunction,
    renderNotificationFunction,
    key,
    extraSpacingForOutline = false
  ) {
    const { showAllNotifications } = this.state;
    const notificationsToShow = notifications.filter(
      (notification) => showAllNotifications || !notification.getIsRead()
    );
    if (notificationsToShow.length === 0) return null;
    return (
      <NotificationsWithHeaderAnimation
        key={key}
        variants={NotificationSectionVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      >
        <NotificationItemCategoryText
          hidden={
            !(
              showAllNotifications ||
              notifications.some((notification) => !notification.getIsRead())
            )
          }
        >
          {renderHeaderFunction()}
        </NotificationItemCategoryText>
        <NotificationsOnDateList $extraSpacingForOutline={extraSpacingForOutline}>
          {notificationsToShow.map((notification) => {
            const notifKey = `${notification.getNotificationIdWithMessageHash()}-ondatelist-${key}`;
            return (
              <NotificationsOnDateListItem key={notifKey}>
                {renderNotificationFunction(notification)}
              </NotificationsOnDateListItem>
            );
          })}
        </NotificationsOnDateList>
      </NotificationsWithHeaderAnimation>
    );
  }

  renderRosterPublicationDateChangeNotifications() {
    return this.renderNotificationsWithHeader(
      this.props.rosternotifications.getPublishAndRevokeNotifications(),
      () => <Meta as="span">{i18n('Roster Publication')}</Meta>,
      this.renderRosterPublicationNotification,
      'rosterpublicationdatechange'
    );
  }

  renderUpdateNotifications() {
    const { updatenotifications } = this.props;
    const extraSpacingForOutline = true;
    return this.renderNotificationsWithHeader(
      updatenotifications.getNotifications(),
      () => (
        <>
          <Meta as="span">{`${i18n('Update')} `}</Meta>
          <span role="img" aria-label="update">
            👋
          </span>
        </>
      ),
      this.renderUpdateNotification,
      'updatenotifications',
      extraSpacingForOutline
    );
  }

  hasUnreadNotifications() {
    const { rosternotifications, updatenotifications, timesheetnotifications } =
      this.props;
    return (
      rosternotifications.hasUnreadNotifications() ||
      updatenotifications.hasUnreadNotifications() ||
      timesheetnotifications.hasUnreadNotifications()
    );
  }

  hasNoNotifications() {
    const { rosternotifications, updatenotifications, timesheetnotifications } =
      this.props;
    return (
      rosternotifications.hasNoNotifications() &&
      updatenotifications.hasNoNotifications() &&
      timesheetnotifications.hasNoNotifications()
    );
  }

  renderNotifications() {
    const { showAllNotifications } = this.state;
    const { rosternotifications, updatenotifications, timesheetnotifications } =
      this.props;
    let listContent = [];
    if (
      (!this.hasUnreadNotifications() && !showAllNotifications) ||
      (showAllNotifications && this.hasNoNotifications())
    )
      listContent = [this.renderNoNotifications()];
    if (rosternotifications.shouldShowWidgetChangeNotifications(showAllNotifications)) {
      listContent = [listContent, this.renderRosterChangeNotifications()];
    }
    if (
      timesheetnotifications.shouldShowWidgetChangeNotifications(showAllNotifications)
    ) {
      listContent = [listContent, this.renderTimesheetChangeNotifications()];
    }
    if (
      rosternotifications.shouldShowPublishAndRevokeNotifications(showAllNotifications)
    ) {
      listContent = [
        this.renderRosterPublicationDateChangeNotifications(),
        ...listContent
      ];
    }
    if (updatenotifications.shouldShowUpdateNotifications(showAllNotifications)) {
      listContent = [this.renderUpdateNotifications(), ...listContent];
    }

    return (
      <ContainerWrapper>
        {/* Depending on the presence of updatenotifications, we render the nopublishedroster message above or beneath the list of notifications */}
        {updatenotifications.hasNoNotifications() && this.renderNoPublishedRoster()}
        <NotificationsList key="rosterpublicationchangelist">
          <AnimatePresence>{listContent}</AnimatePresence>
        </NotificationsList>
        {updatenotifications.shouldShowUpdateNotifications() &&
          this.renderNoPublishedRoster()}
      </ContainerWrapper>
    );
  }

  renderNotificationsBar() {
    const { showAllNotifications } = this.state;
    const { markAllNotificationsAsRead } = this.props;
    const calculatedTop = tailwindConfig.extend.headerHeight + 1; // +1 to account for border
    const hasUnreadItems = this.hasUnreadNotifications();

    return (
      <Sticky $top={calculatedTop} $zIndex={100}>
        <NotificationsBar>
          <ContainerWrapper $relative $paddingY={4}>
            <UIBar tw="-mb-4 flex-wrap">
              <Button
                onClick={this.toggleShowAllNotifications}
                size="small"
                color="primary"
                tw="mb-4"
              >
                {showAllNotifications && i18n('Show Unread')}
                {!showAllNotifications && i18n('Show All')}
              </Button>
              <UIBarGrowItem />
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  markAllNotificationsAsRead();
                }}
                size="small"
                color="primary"
                disabled={!hasUnreadItems}
                tw="mb-4"
              >
                {i18n('Mark All As Read')}
              </Button>
            </UIBar>
          </ContainerWrapper>
        </NotificationsBar>
      </Sticky>
    );
  }

  /* Render this message when:
     - teamplanner is active, but the user has no published roster
   */
  renderNoPublishedRoster() {
    const { humanResource, navs } = this.props;
    if (
      humanResource !== null &&
      navs.find((nav) => nav.id === 'roster') &&
      !humanResource.rosterpublisheduntil
    ) {
      return (
        <NotificationCenterNoContent>
          <NoNotificationsIcon icon="notification" />
          <NoNotificationsTitle>
            {`${i18n('Sorry! No roster notifications for you')}…`}
          </NoNotificationsTitle>
          <NoNotificationsText>
            {`${i18n('We can only show them if you have published rosters')}.`}
          </NoNotificationsText>
        </NotificationCenterNoContent>
      );
    }
    return null;
  }

  renderNotificationCenter() {
    const { navigationHistory } = this.props;
    return (
      <ErrorBoundary {...this.props}>
        <Sticky $zIndex={100} $top={0}>
          <Header>
            <HeaderContent>
              <HeaderTitle>{i18n('Notifications')}</HeaderTitle>
              <HeaderActionsRight>
                <Button
                  contentType="icon"
                  size="medium"
                  icon="close"
                  onClick={(e) => {
                    e.preventDefault();
                    navigationHistory.goBack();
                  }}
                  dataAttributes={{
                    'data-test-button': 'closePopover'
                  }}
                />
              </HeaderActionsRight>
            </HeaderContent>
          </Header>
        </Sticky>

        <div>
          {this.renderNotificationsBar()}
          {this.renderNotifications()}
        </div>
      </ErrorBoundary>
    );
  }

  render() {
    const { location, history, navigationHistory } = this.props;
    return (
      <Popover
        renderContent={this.renderNotificationCenter}
        location={location}
        history={history}
        navigationHistory={navigationHistory}
      />
    );
  }
}

_NotificationCenter.propTypes = {
  rosternotifications: PropTypes.shape({
    doFunctionOnWidgetNotificationsForNonEmptyDates: PropTypes.func,
    hasUnreadNotifications: PropTypes.func,
    shouldShowPublishAndRevokeNotifications: PropTypes.func,
    shouldShowWidgetChangeNotifications: PropTypes.func,
    hasNoNotifications: PropTypes.func,
    getPublishAndRevokeNotifications: PropTypes.func
  }).isRequired,
  timesheetnotifications: PropTypes.shape({
    doFunctionOnWidgetNotificationsForNonEmptyDates: PropTypes.func,
    hasUnreadNotifications: PropTypes.func,
    shouldShowPublishAndRevokeNotifications: PropTypes.func,
    shouldShowWidgetChangeNotifications: PropTypes.func,
    hasNoNotifications: PropTypes.func,
    getPublishAndRevokeNotifications: PropTypes.func
  }).isRequired,
  updatenotifications: PropTypes.shape({
    shouldShowUpdateNotifications: PropTypes.func,
    hasNoNotifications: PropTypes.func,
    hasUnreadNotifications: PropTypes.func,
    getNotifications: PropTypes.func
  }).isRequired,
  toggleNotificationUnreadStatus: PropTypes.func.isRequired,
  markAllNotificationsAsRead: PropTypes.func.isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  redirectToUrl: PropTypes.func.isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  humanResource: PropTypes.shape({
    rosterpublisheduntil: YPDateTimeObjectPropType
  }),
  location: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  navs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string
    })
  ).isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired
};

_NotificationCenter.defaultProps = {
  humanResource: null
};

export default withRouter(_NotificationCenter);
